import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { getCachedQuery } from './get-cached-query.js';

interface TerminalRowFence {
  readonly rowKey: string;
  readonly rowIds: Set<string>;
}

export interface TerminalAwareQueryCache {
  readonly getQueryData: (queryKey: QueryKey) => unknown;
  readonly setQueryData: (queryKey: QueryKey, updater: (previous: unknown) => unknown) => void;
}

// Keying by TanStack's Query object keeps each fence scoped to one cache entry
// and lets query GC release it without a separate lifecycle subscription.
const terminalRowsByQuery = new WeakMap<object, TerminalRowFence>();

export function recordTerminalRow(
  queryClient: QueryClient,
  queryKey: QueryKey,
  rowKey: string,
  rowId: string,
): void {
  const query = getCachedQuery(queryClient, { queryKey });
  if (query === undefined) return;
  const existing = terminalRowsByQuery.get(query);
  if (existing !== undefined && existing.rowKey === rowKey) {
    existing.rowIds.add(rowId);
    return;
  }
  terminalRowsByQuery.set(query, { rowKey, rowIds: new Set([rowId]) });
}

export function filterTerminalRows<T>(queryClient: QueryClient, queryKey: QueryKey, data: T): T {
  const query = getCachedQuery(queryClient, { queryKey });
  const fence = query === undefined ? undefined : terminalRowsByQuery.get(query);
  if (fence === undefined || data === null || typeof data !== 'object') return data;
  const rows = (data as { readonly data?: unknown }).data;
  if (!Array.isArray(rows)) return data;
  const filtered = rows.filter((row) => !isTerminalRow(row, fence));
  if (filtered.length === rows.length) return data;
  return { ...data, data: filtered };
}

export function terminalAwareQueryCache(queryClient: QueryClient): TerminalAwareQueryCache {
  return {
    getQueryData: (queryKey) => queryClient.getQueryData(queryKey),
    setQueryData: (queryKey, updater) => {
      queryClient.setQueryData(queryKey, (previous) =>
        filterTerminalRows(queryClient, queryKey, updater(previous)),
      );
    },
  };
}

function isTerminalRow(row: unknown, fence: TerminalRowFence): boolean {
  if (row === null || typeof row !== 'object') return false;
  return fence.rowIds.has(String((row as Record<string, unknown>)[fence.rowKey]));
}
